import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import notFoundImage from '../../assets/fox-not-found.webp';
import { getImageUrl } from '../../lib/helpers';

/**
 * Foto de socio: miniatura pequeña + popover con la foto GRANDE al pasar el ratón
 * (desktop) o tocar (tablet). Pensado para listas donde la cara es difícil de
 * identificar (Entradas, Nueva entrada, Dispensario, dropdown de Productos).
 *
 * - `path`: documents_path / image_path del socio (clave o URL). Si falta → fox.
 * - `name`: se muestra bajo la foto grande.
 * - `className`: clases de la miniatura (tamaño/forma).
 * - `size`: lado del popover en px.
 *
 * El popover se renderiza en un portal (document.body) para que no lo recorte el
 * overflow de tablas/listas. pointer-events:none para no interferir con la fila.
 */
export default function MemberPhoto({
  path,
  name = '',
  className = 'h-10 w-10 rounded-full object-cover',
  size = 320,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const src = path ? getImageUrl(path) : notFoundImage;

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = r.right + 8;                 // por defecto a la derecha de la miniatura
    if (left + size > window.innerWidth - 8) left = r.left - size - 8; // si no cabe, a la izquierda
    if (left < 8) left = 8;
    let top = r.top + r.height / 2 - size / 2;
    top = Math.max(8, Math.min(top, window.innerHeight - size - 48));
    setPos({ top, left });
  }, [size]);

  const show = useCallback(() => { place(); setOpen(true); }, [place]);
  const hide = useCallback(() => setOpen(false), []);

  // Cerrar al tocar fuera (tablet) o al hacer scroll. El click que abre no debe
  // cerrarlo en el mismo tick → se difiere el alta del listener.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const t = setTimeout(() => document.addEventListener('click', close, { once: true }), 0);
    window.addEventListener('scroll', close, true);
    return () => { clearTimeout(t); document.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [open]);

  return (
    <>
      <img
        ref={ref}
        src={src}
        alt={name}
        onError={(e) => { e.target.src = notFoundImage; }}
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={(e) => { e.stopPropagation(); if (open) hide(); else show(); }}
        className={`${className} cursor-zoom-in`}
      />
      {open && createPortal(
        <div
          className="fixed z-[100] pointer-events-none rounded-xl border border-[#E5E5E5] bg-white p-1 shadow-2xl"
          style={{ top: pos.top, left: pos.left, width: size }}
        >
          <img
            src={src}
            alt={name}
            onError={(e) => { e.target.src = notFoundImage; }}
            className="w-full rounded-lg object-cover"
            style={{ height: size }}
          />
          {name && (
            <div className="px-1 pt-1 text-center text-xs font-medium text-[#0A0A0A] truncate">{name}</div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
